import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Icon from "@mdi/react";
import { mdiSchool, mdiMapMarker, mdiMagnify, mdiClose, mdiCheckCircle, mdiCrosshairsGps } from "@mdi/js";
import { organizationApi } from "../api/organization";
import { useAuth } from "../contexts/AuthContext";
import StoreBanner from "../components/store-banner";
import { useMobileMenu } from "../hooks/useMobileMenu";
import danceLocaleIcon from "../assets/dance_locale_3-200h.png";
import SearchTypeSwitcher from "../components/SearchTypeSwitcher";
import SchoolCard from "../components/SchoolCard";
import { getDanceStyles, resolveToId } from "../config/danceStyles";
import "./SchoolsDirectory.css";
import useRememberedSearchLocation, { readRememberedLocation } from "../hooks/useRememberedSearchLocation";

const NEAR_ME_RADIUS_METERS = 20000;

const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const SchoolsDirectory = () => {
    useMobileMenu();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();

    const [searchQuery, setSearchQuery] = useState(searchParams.get("query") || "");
    // A ?location= in the URL always wins (a shared link means THAT place); only
    // fall back to what this browser last searched.
    const urlLocation = searchParams.get("location") || "";
    const remembered = urlLocation ? null : readRememberedLocation();
    const [searchLocation, setSearchLocation] = useState(urlLocation || remembered?.location || "");
    const [allSchools, setAllSchools] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [selectedStyle, setSelectedStyle] = useState("");
    const [nearMe, setNearMe] = useState(false);

    const [userLocation, setUserLocation] = useState(null);
    const [locationLoading, setLocationLoading] = useState(false);
    // Text filters apply on Search submit; style + Near Me re-filter live.
    const [appliedQuery, setAppliedQuery] = useState(searchParams.get("query") || "");
    // Seeded the same way as searchLocation: otherwise the box would show a
    // remembered place that was never actually applied to the results.
    const [appliedLocation, setAppliedLocation] = useState(urlLocation || remembered?.location || "");
    // Remember the location for next visit. Persists appliedLocation — the value that
    // actually drives the results — so what is restored is what was really searched.
    useRememberedSearchLocation({
        location: appliedLocation,
        urlHadLocation: Boolean(urlLocation),
    });

    const handleSearch = async e => {
        if (e) e.preventDefault();

        setLoading(true);
        setHasSearched(true);
        setAppliedQuery(searchQuery);
        setAppliedLocation(searchLocation);

        let data;
        try {
            data = await organizationApi.getSchools();
        } catch (err) {
            toast.error(err.message || "Failed to load schools.");
            setLoading(false);
            return;
        }

        if (!data.success) {
            toast.error(data.message || "Failed to load schools.");
            setLoading(false);
            return;
        }

        setAllSchools(data.schools || []);
        setLoading(false);
    };

    // Auto-search on page load (all schools, or pre-filtered when URL params present)
    useEffect(() => {
        handleSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleNearMe = () => {
        if (!nearMe && !userLocation) {
            setLocationLoading(true);
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    setUserLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
                    setNearMe(true);
                    setLocationLoading(false);
                },
                () => {
                    toast.error('Location access denied. Enable location permissions to use Near Me.');
                    setLocationLoading(false);
                }
            );
        } else {
            setNearMe(prev => !prev);
        }
    };

    const schools = useMemo(() => {
        let results = allSchools;

        if (appliedQuery) {
            const q = appliedQuery.toLowerCase();
            results = results.filter(
                s =>
                    (s.name || "").toLowerCase().includes(q) ||
                    (s.location?.description || "").toLowerCase().includes(q)
            );
        }

        if (appliedLocation) {
            const loc = appliedLocation.toLowerCase();
            results = results.filter(s => (s.location?.description || "").toLowerCase().includes(loc));
        }

        if (selectedStyle) {
            results = results.filter(s =>
                (s.styles || []).some(st => resolveToId(st) === selectedStyle)
            );
        }

        if (nearMe && userLocation) {
            results = results.filter(s =>
                s.latitude !== null && s.latitude !== undefined &&
                s.longitude !== null && s.longitude !== undefined &&
                calculateDistance(userLocation.latitude, userLocation.longitude, s.latitude, s.longitude) <= NEAR_ME_RADIUS_METERS
            );
        }

        return results;
    }, [allSchools, appliedQuery, appliedLocation, selectedStyle, nearMe, userLocation]);

    const clearSearch = () => {
        setSearchQuery("");
        setSearchLocation("");
        setSelectedStyle("");
        setNearMe(false);
        setAppliedQuery("");
        setAppliedLocation("");
    };

    return (
        <div className="schools-directory">
            <div data-role="Header" className="home-header-container">
                <header className="home-header">
                    <a href="/" className="home-logo1">
                        <img alt="logo" src={danceLocaleIcon} className="home-image1" />
                        <span className="nav-wordmark">Dance Localé</span>
                    </a>
                    <div className="home-menu1">
                        <a href="/" className="home-text10" style={{ textDecoration: "none" }}>
                            Home
                        </a>
                        <a
                            href="/instructors"
                            className="home-text11"
                            style={{ color: "var(--dl-color-orange-default)", textDecoration: "none" }}
                        >
                            Search
                        </a>
                        {user && user.organisationId && (
                            <a href="/dashboard" className="home-text12" style={{ textDecoration: "none" }}>
                                Dashboard
                            </a>
                        )}
                        {user && (
                            <a href="/my-hub" className="home-text12" style={{ textDecoration: "none" }}>
                                My Hub
                            </a>
                        )}
                        <a href="/contact" className="home-text13" style={{ textDecoration: "none" }}>
                            Contact
                        </a>
                    </div>
                    <div className="home-container11">
                        <StoreBanner />
                    </div>
                    <div data-role="BurgerMenu" className="home-burger-menu">
                        <svg viewBox="0 0 1024 1024" className="home-menu2">
                            <path d="M810.667 725.333h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 426.667h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 128h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                        </svg>
                    </div>
                </header>

                {/* Mobile Menu */}
                <div data-role="MobileMenu" className="home-mobile-menu">
                    <div className="home-top">
                        <div className="home-logo2">
                            <img alt="logo" src={danceLocaleIcon} className="home-image2" />
                        </div>
                        <div data-role="CloseMobileMenu" className="home-close-menu">
                            <svg viewBox="0 0 1024 1024" className="home-icon">
                                <path d="M810 274l-238 238 238 238-60 60-238-238-238 238-60-60 238-238-238-238 60-60 238 238 238-238z"></path>
                            </svg>
                        </div>
                    </div>
                    <div className="home-mid">
                        <a href="/" className="home-text14">
                            Home
                        </a>
                        <a href="/instructors" className="home-text15" style={{ color: "var(--dl-color-orange-default)" }}>
                            Search
                        </a>
                        {user && user.organisationId && (
                            <a href="/dashboard" className="home-text16">
                                Dashboard
                            </a>
                        )}
                        {user && (
                            <a href="/my-hub" className="home-text16">
                                My Hub
                            </a>
                        )}
                        <a href="/contact" className="home-text17">
                            Contact
                        </a>
                    </div>
                </div>
            </div>

            {/* Hero Search Section */}
            <div className="search-hero">
                <div
                    style={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "6px",
                    }}
                >
                    {/* <button
                        onClick={() => navigate("/")}
                        style={{
                            position: "absolute",
                            left: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            background:
                                "linear-gradient(135deg, var(--dl-color-purple-default) 0%, var(--dl-color-purple-1000) 100%)",
                            border: "1px solid var(--dl-color-purple-default)",
                            color: "white",
                            borderRadius: "18px",
                            padding: "8px 16px",
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            cursor: "pointer",
                        }}
                    >
                        ← Back to Home
                    </button> */}
                    <h1 style={{ margin: 0 }}>Find Dance Schools</h1>
                </div>
                <p>Discover dance schools in your area offering classes, workshops, and private lessons</p>


                <form className="search-form" onSubmit={handleSearch}>
                    <div className="search-inputs">
                        <SearchTypeSwitcher active="schools" />

                        <div className="search-input-wrapper">
                            <Icon path={mdiSchool} size={1} className="search-input-icon" />
                            <input
                                type="text"
                                placeholder="School name..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="search-input"
                            />
                        </div>

                        <div className="search-input-wrapper">
                            <Icon path={mdiMapMarker} size={1} className="search-input-icon" />
                            <input
                                type="text"
                                placeholder="City or region..."
                                value={searchLocation}
                                onChange={e => setSearchLocation(e.target.value)}
                                className="search-input"
                            />
                        </div>

                        <button type="submit" className="btn-search" disabled={loading}>
                            <Icon path={mdiMagnify} size={1} />
                            {loading ? "Searching..." : "Search"}
                        </button>
                    </div>

                    <div className="filter-chips">
                        <select
                            className="filter-select"
                            value={selectedStyle}
                            onChange={e => setSelectedStyle(e.target.value)}
                        >
                            <option value="">All styles</option>
                            {getDanceStyles().map(style => (
                                <option key={style.id} value={style.id}>
                                    {style.label}
                                </option>
                            ))}
                        </select>

                        <button
                            type="button"
                            className={`filter-chip ${nearMe ? "active" : ""}`}
                            onClick={toggleNearMe}
                            disabled={locationLoading}
                        >
                            <Icon path={mdiCrosshairsGps} size={0.8} />
                            {locationLoading ? "Locating..." : "Near Me (20km)"}
                            {nearMe && <Icon path={mdiCheckCircle} size={0.7} />}
                        </button>

                        {(searchQuery || searchLocation || selectedStyle || nearMe) && (
                            <button type="button" onClick={clearSearch} className="btn-clear">
                                <Icon path={mdiClose} size={0.8} />
                                Clear
                            </button>
                        )}
                    </div>
                </form>
            </div>

            {/* Results Section */}
            <div className="results-container">
                {loading && (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Searching for schools...</p>
                    </div>
                )}

                {!loading && hasSearched && schools.length === 0 && (
                    <div className="empty-state">
                        <Icon path={mdiSchool} size={3} color="#ccc" />
                        <h3>No schools found</h3>
                        <p>Try adjusting your search terms or filters</p>
                    </div>
                )}

                {!loading && schools.length > 0 && (
                    <>
                        <div className="schools-grid">
                            {schools.map(school => (
                                <SchoolCard key={school.id} school={school} linkTo />
                            ))}
                        </div>
                    </>
                )}

                {/* CTA Section */}
                {!loading && hasSearched && (
                    <div className="cta-section">
                        <h2>Are you a dance school?</h2>
                        <p>Register your school on Dance Localé to reach more students and grow your community</p>
                        <div className="cta-store-buttons">
                            <Link
                                to="/register"
                                className="home-cta-secondary"
                                style={{ color: "white", borderColor: "white" }}
                            >
                                Register your school →
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SchoolsDirectory;
